import { IAuthRepository } from '../repositories/IAuthRepository';
import { AuthLocalStorageAdapter } from '../repositories/AuthLocalStorageAdapter';
import { User } from '../models/User';
import { Session } from '../models/Session';
import { AuthToken } from '../value-objects/AuthToken';
import { LoginAttempt } from '../value-objects/LoginAttempt';
import { Email } from '../value-objects/Email';
import { UserId } from '../value-objects/UserId';
import { SessionId } from '../value-objects/SessionId';
import { UserRole } from '../value-objects/UserRole';

/**
 * 인증 결과 타입
 */
export interface AuthenticationResult {
  success: boolean;
  user?: User;
  token?: AuthToken;
  session?: Session;
  error?: string;
  locked?: boolean;
  needsVerification?: boolean;
}

/**
 * 로그인 옵션
 */
export interface LoginOptions {
  rememberMe?: boolean;
  deviceXCircle?: {
    userAgent: string;
    ipAddress: string;
  };
}

/**
 * Auth 도메인 서비스
 * 인증/인가 관련 비즈니스 로직을 처리
 */
export class AuthService {
  constructor(private readonly authRepository: IAuthRepository) {}

  /**
   * 사용자 로그인
   */
  async login(
    email: Email,
    password: string,
    options: LoginOptions = {}
  ): Promise<AuthenticationResult> {
    const { rememberMe = false, deviceXCircle } = options;

    try {
      // 1. 로그인 시도 기록 생성
      const loginAttempt = new LoginAttempt({
        email: email.value,
        timestamp: new Date(),
        success: false,
        ipAddress: deviceXCircle?.ipAddress || 'unknown',
        userAgent: deviceXCircle?.userAgent || 'unknown',
      });

      // 2. 사용자 조회
      const user = await this.authRepository.findUserByEmail(email);
      if (!user) {
        await this.recordFailedLogin(loginAttempt);
        return {
          success: false,
          error: '이메일 또는 비밀번호가 올바르지 않습니다.',
        };
      }

      // 3. 계정 잠금 상태 확인
      if (user.isLocked()) {
        await this.recordFailedLogin(loginAttempt);
        return {
          success: false,
          error: '계정이 잠겨있습니다. 잠시 후 다시 시도해주세요.',
          locked: true,
        };
      }

      // 4. 계정 활성화 상태 확인
      if (!user.isActive) {
        await this.recordFailedLogin(loginAttempt);
        return {
          success: false,
          error: '비활성화된 계정입니다. 관리자에게 문의하세요.',
        };
      }

      // 5. 인증 시도
      const authResult = await this.authRepository.authenticate(email, password);
      if (!authResult) {
        // 실패한 로그인 시도 기록
        await this.recordFailedLogin(loginAttempt);
        
        // 사용자 모델에 실패 기록 추가
        user.recordFailedLogin(loginAttempt);
        await this.authRepository.updateUser(user);

        return {
          success: false,
          error: '이메일 또는 비밀번호가 올바르지 않습니다.',
        };
      }

      // 6. 성공한 로그인 시도 기록
      const successfulAttempt = new LoginAttempt({
        email: email.value,
        timestamp: new Date(),
        success: true,
        ipAddress: deviceXCircle?.ipAddress || 'unknown',
        userAgent: deviceXCircle?.userAgent || 'unknown',
      });
      await this.authRepository.recordLoginAttempt(successfulAttempt);

      // 7. 사용자 로그인 정보 업데이트
      authResult.user.recordSuccessfulLogin();
      await this.authRepository.updateUser(authResult.user);

      // 8. 로컬 스토리지에 저장
      AuthLocalStorageAdapter.saveToken(authResult.token);
      AuthLocalStorageAdapter.saveUser(authResult.user);
      AuthLocalStorageAdapter.saveSession(authResult.session);
      
      // Remember Me 설정
      if (rememberMe) {
        AuthLocalStorageAdapter.saveSecuritySettings({
          rememberMe: true,
          autoLogoutMinutes: 60 * 24 * 7, // 7일
          twoFactorEnabled: false,
          allowMultipleSessions: false,
        });
      }

      // 9. 마지막 활동 시간 업데이트
      AuthLocalStorageAdapter.updateLastActivity();

      // 10. 보안 이벤트 기록
      await this.authRepository.recordSecurityEvent({
        userId: authResult.user.id,
        eventType: 'USER_LOGIN',
        description: '사용자 로그인 성공',
        ipAddress: deviceXCircle?.ipAddress,
        userAgent: deviceXCircle?.userAgent,
      });

      return {
        success: true,
        user: authResult.user,
        token: authResult.token,
        session: authResult.session,
      };
    } catch (error) {
      console.error('로그인 처리 중 오류:', error);
      return {
        success: false,
        error: '로그인 처리 중 오류가 발생했습니다.',
      };
    }
  }

  /**
   * 사용자 로그아웃
   */
  async logout(sessionId?: SessionId): Promise<void> {
    try {
      // 1. 현재 세션 정보 가져오기
      const currentSession = AuthLocalStorageAdapter.loadSession();
      const currentUser = AuthLocalStorageAdapter.loadUser();
      
      const logoutSessionId = sessionId || 
        (currentSession ? SessionId.fromString(currentSession.id) : undefined);

      // 2. 서버에서 세션 무효화
      if (logoutSessionId) {
        await this.authRepository.logout(logoutSessionId);
      }

      // 3. 로컬 스토리지 정리
      AuthLocalStorageAdapter.clearAll();

      // 4. 보안 이벤트 기록 (세션이 있는 경우만)
      if (currentUser && logoutSessionId) {
        await this.authRepository.recordSecurityEvent({
          userId: UserId.fromString(currentUser.id),
          eventType: 'USER_LOGOUT',
          description: '사용자 로그아웃',
        });
      }
    } catch (error) {
      console.error('로그아웃 처리 중 오류:', error);
      // 로그아웃은 실패해도 로컬 정리는 수행
      AuthLocalStorageAdapter.clearAll();
    }
  }

  /**
   * 모든 세션에서 로그아웃
   */
  async logoutFromAllDevices(userId: UserId): Promise<void> {
    try {
      // 1. 서버에서 모든 세션 무효화
      await this.authRepository.logoutAllSessions(userId);

      // 2. 로컬 스토리지 정리
      AuthLocalStorageAdapter.clearAll();

      // 3. 보안 이벤트 기록
      await this.authRepository.recordSecurityEvent({
        userId,
        eventType: 'USER_LOGOUT_ALL',
        description: '모든 기기에서 로그아웃',
      });
    } catch (error) {
      console.error('전체 로그아웃 처리 중 오류:', error);
      AuthLocalStorageAdapter.clearAll();
      throw error;
    }
  }

  /**
   * 토큰 갱신
   */
  async refreshAuthToken(): Promise<AuthToken | null> {
    try {
      const currentToken = AuthLocalStorageAdapter.loadToken();
      if (!currentToken || !currentToken.refreshToken) {
        return null;
      }

      const result = await this.authRepository.refreshToken(currentToken.refreshToken);
      if (!result) {
        return null;
      }

      // 새 토큰과 세션 정보 저장
      AuthLocalStorageAdapter.saveToken(result.token);
      AuthLocalStorageAdapter.saveSession(result.session);
      AuthLocalStorageAdapter.updateLastActivity();

      return result.token;
    } catch (error) {
      console.error('토큰 갱신 중 오류:', error);
      return null;
    }
  }

  /**
   * 현재 인증 상태 확인
   */
  isAuthenticated(): boolean {
    return AuthLocalStorageAdapter.isAuthenticated();
  }

  /**
   * 세션 유효성 검사 및 자동 처리
   */
  async validateAndRefreshSession(): Promise<{
    isValid: boolean;
    user?: User;
    needsLogin?: boolean;
  }> {
    try {
      const validation = AuthLocalStorageAdapter.validateSession();

      if (validation.shouldLogout) {
        await this.logout();
        return {
          isValid: false,
          needsLogin: true,
        };
      }

      if (validation.needsRefresh) {
        const newToken = await this.refreshAuthToken();
        if (!newToken) {
          await this.logout();
          return {
            isValid: false,
            needsLogin: true,
          };
        }
      }

      if (validation.isValid) {
        const userData = AuthLocalStorageAdapter.loadUser();
        if (userData) {
          // 간단한 User 객체 재구성 (전체 매핑은 Repository에서)
          return {
            isValid: true,
            user: userData as any, // 실제로는 적절한 매핑 필요
          };
        }
      }

      return { isValid: false };
    } catch (error) {
      console.error('세션 검증 중 오류:', error);
      return {
        isValid: false,
        needsLogin: true,
      };
    }
  }

  /**
   * 현재 사용자 정보 가져오기
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const token = AuthLocalStorageAdapter.loadToken();
      if (!token || token.isExpired()) {
        return null;
      }

      const user = await this.authRepository.findUserByToken(token.accessToken);
      if (user) {
        // 최신 사용자 정보를 로컬 스토리지에 업데이트
        AuthLocalStorageAdapter.saveUser(user);
      }

      return user;
    } catch (error) {
      console.error('현재 사용자 조회 중 오류:', error);
      return null;
    }
  }

  /**
   * 실패한 로그인 시도 기록
   */
  private async recordFailedLogin(attempt: LoginAttempt): Promise<void> {
    try {
      await this.authRepository.recordLoginAttempt(attempt);
      AuthLocalStorageAdapter.saveLoginAttempt(attempt);
    } catch (error) {
      console.error('실패한 로그인 기록 중 오류:', error);
    }
  }

  /**
   * 계정 잠금 해제 (관리자용)
   */
  async unlockAccount(userId: UserId): Promise<void> {
    await this.authRepository.unlockAccount(userId);
    
    await this.authRepository.recordSecurityEvent({
      userId,
      eventType: 'ACCOUNT_UNLOCKED',
      description: '관리자에 의한 계정 잠금 해제',
    });
  }

  /**
   * 비밀번호 변경
   */
  async changePassword(
    userId: UserId,
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // 1. 현재 사용자 조회
      const user = await this.authRepository.findUserById(userId);
      if (!user) {
        return {
          success: false,
          error: '사용자를 찾을 수 없습니다.',
        };
      }

      // 2. 현재 비밀번호 확인 (실제로는 해시 비교 필요)
      const authResult = await this.authRepository.authenticate(user.email, currentPassword);
      if (!authResult) {
        return {
          success: false,
          error: '현재 비밀번호가 올바르지 않습니다.',
        };
      }

      // 3. 새 비밀번호로 변경 (실제로는 해싱 필요)
      await this.authRepository.changePassword(userId, newPassword);

      // 4. 보안 이벤트 기록
      await this.authRepository.recordSecurityEvent({
        userId,
        eventType: 'PASSWORD_CHANGED',
        description: '사용자가 비밀번호를 변경했습니다',
      });

      return { success: true };
    } catch (error) {
      console.error('비밀번호 변경 중 오류:', error);
      return {
        success: false,
        error: '비밀번호 변경 중 오류가 발생했습니다.',
      };
    }
  }

  /**
   * 활동 시간 업데이트 (세션 유지)
   */
  updateActivity(): void {
    AuthLocalStorageAdapter.updateLastActivity();
  }

  /**
   * 보안 설정 업데이트
   */
  updateSecuritySettings(settings: {
    autoLogoutMinutes?: number;
    twoFactorEnabled?: boolean;
    allowMultipleSessions?: boolean;
  }): void {
    const currentSettings = AuthLocalStorageAdapter.loadSecuritySettings();
    const newSettings = {
      ...currentSettings,
      ...settings,
    };
    AuthLocalStorageAdapter.saveSecuritySettings(newSettings);
  }
}